import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDraft } from '../hooks/useDraft';
import DraftStatusBadge from './DraftStatusBadge';
import { titleCaseColor } from '../lib/utils';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import api from '../lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SIZE_ORDER = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL','7XL','8XL','9XL','Free Size','32','34','36','38','40','42'];

const SizeGroupPricing = ({ sizes, onApply }) => {
  const [groups, setGroups] = useState([{ sizes: [], cost: '', qty: '' }]);
  const addGroup = () => setGroups(prev => [...prev, { sizes: [], cost: '', qty: '' }]);
  const removeGroup = (idx) => setGroups(prev => prev.filter((_, i) => i !== idx));
  const toggleSize = (gIdx, size) => {
    setGroups(prev => prev.map((g, i) => {
      if (i !== gIdx) return g;
      const has = g.sizes.includes(size);
      return { ...g, sizes: has ? g.sizes.filter(s => s !== size) : [...g.sizes, size] };
    }));
  };
  const updateCost = (gIdx, cost) => setGroups(prev => prev.map((g, i) => i === gIdx ? { ...g, cost } : g));
  const updateQty = (gIdx, qty) => setGroups(prev => prev.map((g, i) => i === gIdx ? { ...g, qty } : g));
  const usedSizes = groups.flatMap(g => g.sizes);
  const apply = () => {
    const mapping = {};
    for (const g of groups) { const cost = parseFloat(g.cost); const qty = parseInt(g.qty) || 0; if ((!cost && !qty) || g.sizes.length === 0) continue; for (const s of g.sizes) mapping[s] = { cost: cost || 0, qty }; }
    if (Object.keys(mapping).length === 0) { toast.error('Set at least one size group with cost or qty'); return; }
    onApply(mapping);
  };
  return (
    <div className="space-y-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold text-amber-800">Size-wise Cost & Qty Groups</Label>
        <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-amber-700" onClick={addGroup}>+ Add Group</Button>
      </div>
      <p className="text-xs text-amber-600">e.g. M-2XL at one rate & qty, 3XL-5XL at another</p>
      {groups.map((g, gIdx) => (
        <div key={gIdx} className="flex items-start gap-2 bg-white p-2 rounded border">
          <div className="flex-1">
            <div className="flex flex-wrap gap-1 mb-2">
              {sizes.map(s => {
                const usedElsewhere = usedSizes.includes(s) && !g.sizes.includes(s);
                return (<button key={s} type="button" disabled={usedElsewhere} onClick={() => toggleSize(gIdx, s)}
                  className={`px-2 py-0.5 rounded text-xs border transition-colors ${g.sizes.includes(s) ? 'bg-amber-500 text-white border-amber-500' : usedElsewhere ? 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-600 border-gray-300 hover:border-amber-400'}`}>{s}</button>);
              })}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Cost/pc:</span>
                <Input type="number" className="h-7 w-24 text-xs" placeholder="0" value={g.cost} onChange={e => updateCost(gIdx, e.target.value)} min="0" data-testid={`po-group-cost-${gIdx}`} />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500">Qty:</span>
                <Input type="number" className="h-7 w-20 text-xs" placeholder="0" value={g.qty} onChange={e => updateQty(gIdx, e.target.value)} min="0" data-testid={`po-group-qty-${gIdx}`} />
              </div>
            </div>
          </div>
          {groups.length > 1 && (<button type="button" onClick={() => removeGroup(gIdx)} className="text-red-400 hover:text-red-600 mt-1"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg></button>)}
        </div>
      ))}
      <Button type="button" size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={apply} data-testid="po-apply-group">Apply Cost & Qty to Variants</Button>
    </div>
  );
};

const CreatePurchaseOrder = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const editPO = location.state?.editPO || null;

  // Master data
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [enums, setEnums] = useState({ sizes: [], colors: [], fabrics: [], categories: [], design_themes: [] });
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  // PO Creation State
  const [selectedVendor, setSelectedVendor] = useState('');
  const [transportName, setTransportName] = useState('');
  const [poDate, setPoDate] = useState('');
  const [poItems, setPoItems] = useState([]);
  const [poNotes, setPoNotes] = useState('');
  const [addMode, setAddMode] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [newProduct, setNewProduct] = useState({ design_code: '', category: '', brand_id: '', design_theme: [], fabric: '', images: [] });

  // Inline vendor creation
  const [showInlineVendor, setShowInlineVendor] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: '', contact_person: '', phone: '', address: '', city: '', state: '', pincode: '', area: '', email: '', gst_number: '' });
  const [savingVendor, setSavingVendor] = useState(false);

  // Draft auto-save
  const { draftStatus, save: saveDraftData, clear: clearDraft } = useDraft('PO', { enabled: !editPO, initialDraftId: location.state?.draftId || null });

  // Vendor search
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const vendorSearchRef = useRef(null);

  // Repeat order search
  const [repeatSearch, setRepeatSearch] = useState('');

  // Inline "Add New" enum
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [newOccasionInput, setNewOccasionInput] = useState('');
  const [newFabricInput, setNewFabricInput] = useState('');
  const [newColorInput, setNewColorInput] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);

  const getBrandName = (id) => brands.find(b => b.id === id)?.name || '';

  useEffect(() => {
    loadData();
  }, []);

  // Populate form if editing
  useEffect(() => {
    if (editPO && vendors.length > 0 && !loading) {
      setSelectedVendor(editPO.vendor_id);
      const vend = vendors.find(v => v.id === editPO.vendor_id);
      setVendorSearch(vend ? vend.name + (vend.city ? ` (${vend.city})` : '') : '');
      setTransportName(editPO.transport_name || '');
      setPoDate(editPO.po_date || '');
      setPoNotes(editPO.notes || '');
      setPoItems((editPO.items || []).map(item => {
        const selSizes = [...new Set((item.variants || []).map(v => v.size))];
        const selColors = [...new Set((item.variants || []).map(v => v.color))];
        return { ...item, selectedSizes: selSizes, selectedColors: selColors };
      }));
    }
  }, [editPO, vendors, loading]);

  const loadData = async () => {
    try {
      const [vendorsRes, productsRes, enumsRes, brandsRes] = await Promise.all([
        api.get('/vendors'), api.get('/products'), api.get('/enums'), api.get('/brands'),
      ]);
      setVendors(vendorsRes.data);
      setProducts(productsRes.data);
      setEnums(enumsRes.data);
      setBrands(brandsRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  // Click outside handler for vendor dropdown
  useEffect(() => {
    const handler = (e) => {
      if (vendorSearchRef.current && !vendorSearchRef.current.contains(e.target)) setVendorDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addCustomEnum = async (type, name, setter) => {
    if (!name.trim()) return;
    const formatted = type === 'colors' ? titleCaseColor(name) : name.trim();
    try {
      await api.post(`/enums/${type}`, { name: formatted });
      toast.success(`"${formatted}" added!`);
      setter('');
      const enumsRes = await api.get('/enums');
      setEnums(enumsRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || `Failed to add ${type}`); }
  };

  const createInlineVendor = async () => {
    if (!newVendor.name || !newVendor.contact_person || !newVendor.phone || !newVendor.address || !newVendor.city || !newVendor.state || !newVendor.pincode) {
      toast.error('Fill all required vendor fields'); return;
    }
    setSavingVendor(true);
    try {
      const res = await api.post('/vendors', newVendor);
      toast.success(`Vendor "${res.data.name}" created!`);
      setSelectedVendor(res.data.id);
      setVendorSearch(res.data.name + (res.data.city ? ` (${res.data.city})` : ''));
      setShowInlineVendor(false);
      setNewVendor({ name: '', contact_person: '', phone: '', address: '', city: '', state: '', pincode: '', area: '', email: '', gst_number: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create vendor'); }
    finally { setSavingVendor(false); }
  };

  const handlePincodeLookup = async (pincode) => {
    if (pincode.length === 6) {
      try {
        const res = await api.get(`/pincode/${pincode}`);
        setNewVendor(prev => ({ ...prev, city: res.data.city || prev.city, state: res.data.state || prev.state, area: res.data.area || prev.area }));
      } catch {}
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData(); formData.append('file', file);
      const res = await api.post('/products/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNewProduct(p => ({ ...p, images: [res.data.url] }));
      toast.success('Image uploaded!');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const addExistingProductToItems = (product) => {
    // Auto-select colours from image_colors and existing SKUs (dedupe case-insensitive)
    const colorsFromImages = Object.values(product.image_colors || {}).filter(c => c && c.trim()).map(c => titleCaseColor(c));
    const colorsFromSKUs = [...new Set((product.skus || []).map(s => s.color).filter(Boolean))].map(c => titleCaseColor(c));
    const autoColors = [...new Set([...colorsFromImages, ...colorsFromSKUs])];

    setPoItems(prev => {
      const lastDeliveryDate = prev.length > 0 ? (prev[prev.length - 1].delivery_date || '') : '';
      return [...prev, {
        product_id: product.id, design_code: product.design_code,
        brand_id: product.brand_id || '', fabric: product.fabric || '',
        delivery_date: lastDeliveryDate, notes: '', variants: [],
        images: product.images || [], image_colors: product.image_colors || {},
        selectedSizes: [], selectedColors: autoColors,
      }];
    });
    setAddMode(null);
    if (autoColors.length > 0) {
      toast.success(`Auto-selected ${autoColors.length} colour(s) from design`);
    }
  };

  // Swap a PO item's design to a different product (used when editing design code in edit mode)
  const swapDesignInItem = (idx, product) => {
    const colorsFromImages = Object.values(product.image_colors || {}).filter(c => c && c.trim()).map(c => titleCaseColor(c));
    const colorsFromSKUs = [...new Set((product.skus || []).map(s => s.color).filter(Boolean))].map(c => titleCaseColor(c));
    const autoColors = [...new Set([...colorsFromImages, ...colorsFromSKUs])];

    setPoItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      return {
        ...item,
        product_id: product.id,
        design_code: product.design_code,
        designSearch: product.design_code,
        showDesignDropdown: false,
        brand_id: product.brand_id || item.brand_id || '',
        fabric: product.fabric || item.fabric || '',
        images: product.images || [],
        image_colors: product.image_colors || {},
        selectedColors: autoColors.length > 0 ? autoColors : item.selectedColors || [],
      };
    }));
    toast.success(`Switched to ${product.design_code}`);
  };

  // Fetch a design from backend when not found in cached products list
  const searchDesignFromBackend = async (designCode, idx) => {
    try {
      const res = await api.get(`/products/search/${encodeURIComponent(designCode)}`);
      const found = res.data;
      if (found && found.length > 0) {
        // Add to local products cache
        setProducts(prev => {
          const ids = new Set(prev.map(p => p.id));
          const newOnes = found.filter(p => !ids.has(p.id));
          return newOnes.length > 0 ? [...prev, ...newOnes] : prev;
        });
        swapDesignInItem(idx, found[0]);
      }
    } catch {
      toast.error(`Design "${designCode}" not found in catalogue`);
    }
  };


  const addNewProductToItems = async () => {
    if (!newProduct.design_code || !newProduct.category || !newProduct.brand_id || newProduct.design_theme.length === 0) {
      toast.error('Fill all required fields (including at least one occasion)'); return;
    }
    try {
      const res = await api.post('/products', {
        design_code: newProduct.design_code.toUpperCase(), category: newProduct.category,
        brand_id: newProduct.brand_id, design_theme: newProduct.design_theme.join(','),
        fabric: newProduct.fabric, images: newProduct.images,
      });
      const created = res.data;
      setPoItems(prev => {
        const lastDeliveryDate = prev.length > 0 ? (prev[prev.length - 1].delivery_date || '') : '';
        return [...prev, {
          product_id: created.id, design_code: created.design_code,
          brand_id: newProduct.brand_id, fabric: newProduct.fabric,
          delivery_date: lastDeliveryDate, notes: '', variants: [], images: created.images || [],
          selectedSizes: [], selectedColors: [],
        }];
      });
      setNewProduct({ design_code: '', category: '', brand_id: '', design_theme: [], fabric: '', images: [] });
      setAddMode(null); loadData();
      toast.success(`Product ${created.design_code} created!`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create product'); }
  };

  const fetchNextDesignNumber = async () => {
    try { const res = await api.get('/purchase-orders/next-design-number'); setNewProduct(p => ({ ...p, design_code: res.data.design_number })); } catch {}
  };

  const updateItemField = (idx, field, value) => {
    setPoItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const toggleSize = (idx, size) => {
    setPoItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const has = item.selectedSizes.includes(size);
      const newSizes = has ? item.selectedSizes.filter(s => s !== size) : [...item.selectedSizes, size];
      return rebuildVariants({ ...item, selectedSizes: newSizes });
    }));
  };

  const toggleColor = (idx, color) => {
    setPoItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const has = item.selectedColors.includes(color);
      const newColors = has ? item.selectedColors.filter(c => c !== color) : [...item.selectedColors, color];
      return rebuildVariants({ ...item, selectedColors: newColors });
    }));
  };

  const rebuildVariants = (item) => {
    const existing = {};
    for (const v of item.variants) existing[`${v.color}_${v.size}`] = v;
    const newVariants = [];
    const sortedColors = [...item.selectedColors];
    const sortedSizes = [...item.selectedSizes].sort((a, b) => SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b));
    for (const color of sortedColors) {
      for (const size of sortedSizes) {
        const key = `${color}_${size}`;
        if (existing[key]) newVariants.push(existing[key]);
        else newVariants.push({ size, color, qty_ordered: 0, tentative_cost: 0 });
      }
    }
    return { ...item, variants: newVariants };
  };

  const updateVariant = (itemIdx, varIdx, field, value) => {
    setPoItems(prev => prev.map((item, i) => {
      if (i !== itemIdx) return item;
      return { ...item, variants: item.variants.map((v, vi) => vi === varIdx ? { ...v, [field]: parseFloat(value) || 0 } : v) };
    }));
  };

  const applySizeGroupCosts = (itemIdx, costQtyMap) => {
    setPoItems(prev => prev.map((item, i) => {
      if (i !== itemIdx) return item;
      return { ...item, variants: item.variants.map(v => {
        const mapping = costQtyMap[v.size];
        if (!mapping) return v;
        const updates = {};
        if (mapping.cost) updates.tentative_cost = mapping.cost;
        if (mapping.qty) updates.qty_ordered = mapping.qty;
        return { ...v, ...updates };
      }) };
    }));
    toast.success('Cost & Qty applied!');
  };

  const removeItem = (idx) => setPoItems(prev => prev.filter((_, i) => i !== idx));
  const calcItemTotal = (item) => (item.variants || []).reduce((s, v) => s + (v.qty_ordered || 0) * (v.tentative_cost || 0), 0);
  const calcTotal = () => poItems.reduce((sum, item) => sum + calcItemTotal(item), 0);
  const calcTotalQty = () => poItems.reduce((sum, item) => sum + (item.variants || []).reduce((s, v) => s + (v.qty_ordered || 0), 0), 0);

  const submitPO = async () => {
    if (!selectedVendor) { toast.error('Select a vendor'); return; }
    if (poItems.length === 0) { toast.error('Add at least one product'); return; }
    if (poItems.some(item => !item.variants || item.variants.length === 0)) { toast.error('Each product needs variants'); return; }
    if (poItems.some(item => !item.delivery_date)) { toast.error('Last Date of Delivery is required for all products'); return; }
    setSubmitting(true);
    try {
      const payload = {
        vendor_id: selectedVendor, transport_name: transportName || null,
        po_date: poDate || null,
        items: poItems.map(item => ({
          product_id: item.product_id, design_code: item.design_code,
          brand_id: item.brand_id || null, fabric: item.fabric,
          delivery_date: item.delivery_date, notes: item.notes,
          variants: item.variants, images: item.images,
          image_colors: item.image_colors || {},
        })),
        notes: poNotes,
      };
      if (editPO) {
        await api.put(`/purchase-orders/${editPO.id}`, payload);
        toast.success(`PO ${editPO.po_number} updated!`);
        clearDraft();
        navigate(`/purchase-order/${editPO.id}`);
      } else {
        const res = await api.post('/purchase-orders', payload);
        toast.success(`PO ${res.data.po_number} created!`);
        clearDraft();
        navigate('/purchase-orders');
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save PO'); }
    finally { setSubmitting(false); }
  };

  const filteredVendors = useMemo(() => {
    if (!vendorSearch.trim()) return [];
    const q = vendorSearch.trim().toLowerCase();
    return vendors.filter(v => v.name?.toLowerCase().includes(q) || v.city?.toLowerCase().includes(q) || (/\d/.test(vendorSearch) && v.phone?.includes(q))).slice(0, 20);
  }, [vendorSearch, vendors]);

  const sortedRepeatProducts = useMemo(() => {
    return [...products].sort((a, b) => {
      const da = a.date_added || ''; const db = b.date_added || '';
      return db.localeCompare(da);
    }).filter(p => {
      if (!repeatSearch.trim()) return true;
      const q = repeatSearch.trim().toLowerCase();
      return p.design_code?.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q) || getBrandName(p.brand_id)?.toLowerCase().includes(q);
    }).slice(0, 60);
  }, [products, repeatSearch, brands]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="p-6 space-y-6 animate-fadeIn max-w-6xl mx-auto" data-testid="create-po-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/purchase-orders')} data-testid="back-to-po-list">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }} data-testid="create-po-title">
            {editPO ? `Edit ${editPO.po_number}` : 'Create Purchase Order'}
          </h1>
          <p className="text-sm text-gray-500">Select vendor, add products with brand, sizes, colors and cost details</p>
        </div>
      </div>

      {/* Vendor & Transport */}
      <Card data-testid="po-vendor-section">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Select Vendor *</Label>
              <div className="relative" ref={vendorSearchRef}>
                <Input value={vendorSearch}
                  onChange={e => {
                    setVendorSearch(e.target.value);
                    setVendorDropdownOpen(!!e.target.value.trim());
                    if (!e.target.value) { setSelectedVendor(''); if (!editPO) setPoItems([]); }
                  }}
                  placeholder="Type to search vendor..."
                  className="h-10 text-sm" data-testid="po-vendor-search"
                  disabled={!!editPO}
                />
                {vendorDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredVendors.map(v => (
                      <div key={v.id} className="px-3 py-2 hover:bg-emerald-50 cursor-pointer text-sm"
                        onClick={() => {
                          setSelectedVendor(v.id);
                          setVendorSearch(v.name + (v.city ? ` (${v.city})` : ''));
                          setVendorDropdownOpen(false);
                          if (!editPO) setPoItems([]);
                        }} data-testid={`po-vendor-option-${v.id}`}>
                        <span className="font-medium">{v.name}</span>
                        {v.city && <span className="text-gray-400 ml-2">({v.city})</span>}
                      </div>
                    ))}
                    <div className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-blue-600 border-t font-medium"
                      onClick={() => { setShowInlineVendor(true); setVendorDropdownOpen(false); }}>
                      + Add New Vendor
                    </div>
                  </div>
                )}
              </div>

              {/* Inline Vendor Creation */}
              {showInlineVendor && (
                <Card className="border-blue-300 bg-blue-50/50 mt-2" data-testid="inline-vendor-form">
                  <CardContent className="p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-semibold text-blue-800">Quick Add Vendor</Label>
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setShowInlineVendor(false)}>Cancel</Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs">Name *</Label><Input className="h-8 text-xs" value={newVendor.name} onChange={e => setNewVendor(p => ({...p, name: e.target.value}))} placeholder="Vendor Name" data-testid="inline-vendor-name" /></div>
                      <div><Label className="text-xs">Contact Person *</Label><Input className="h-8 text-xs" value={newVendor.contact_person} onChange={e => setNewVendor(p => ({...p, contact_person: e.target.value}))} placeholder="Contact Person" data-testid="inline-vendor-contact" /></div>
                      <div><Label className="text-xs">Phone *</Label><Input className="h-8 text-xs" value={newVendor.phone} onChange={e => setNewVendor(p => ({...p, phone: e.target.value}))} placeholder="Phone" data-testid="inline-vendor-phone" /></div>
                      <div><Label className="text-xs">Email</Label><Input className="h-8 text-xs" value={newVendor.email} onChange={e => setNewVendor(p => ({...p, email: e.target.value}))} placeholder="Email" /></div>
                      <div className="col-span-2"><Label className="text-xs">Address *</Label><Input className="h-8 text-xs" value={newVendor.address} onChange={e => setNewVendor(p => ({...p, address: e.target.value}))} placeholder="Full Address" data-testid="inline-vendor-address" /></div>
                      <div><Label className="text-xs">Pincode *</Label><Input className="h-8 text-xs" value={newVendor.pincode} onChange={e => { const v = e.target.value; setNewVendor(p => ({...p, pincode: v})); handlePincodeLookup(v); }} placeholder="6-digit" data-testid="inline-vendor-pincode" /></div>
                      <div><Label className="text-xs">Area</Label><Input className="h-8 text-xs" value={newVendor.area} onChange={e => setNewVendor(p => ({...p, area: e.target.value}))} placeholder="Area" /></div>
                      <div><Label className="text-xs">City *</Label><Input className="h-8 text-xs" value={newVendor.city} onChange={e => setNewVendor(p => ({...p, city: e.target.value}))} placeholder="City" data-testid="inline-vendor-city" /></div>
                      <div><Label className="text-xs">State *</Label><Input className="h-8 text-xs" value={newVendor.state} onChange={e => setNewVendor(p => ({...p, state: e.target.value}))} placeholder="State" data-testid="inline-vendor-state" /></div>
                      <div className="col-span-2"><Label className="text-xs">GST Number</Label><Input className="h-8 text-xs" value={newVendor.gst_number} onChange={e => setNewVendor(p => ({...p, gst_number: e.target.value}))} placeholder="GST Number" /></div>
                    </div>
                    <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 w-full" onClick={createInlineVendor} disabled={savingVendor} data-testid="save-inline-vendor">
                      {savingVendor ? 'Creating...' : 'Create Vendor & Select'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Transport Name</Label>
              <Input value={transportName} onChange={e => setTransportName(e.target.value)} placeholder="e.g. VRL Logistics" className="h-10 text-sm" data-testid="po-transport-name" />
            </div>
            {editPO && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold">PO Date</Label>
                <Input type="date" value={poDate} onChange={e => setPoDate(e.target.value)} className="h-10 text-sm" data-testid="po-date-input" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Product Items */}
      {selectedVendor && (
        <div className="space-y-4">
          {poItems.map((item, idx) => (
            <Card key={idx} className="border-l-4 border-l-emerald-500" data-testid={`po-item-${idx}`}>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2">
                      {(item.images?.length > 0) ? item.images.map((img, imgIdx) => {
                        const colorName = item.image_colors?.[img] || '';
                        return (
                          <div key={imgIdx} className="text-center cursor-pointer" onClick={() => setEnlargedImage(`${API_BASE}${img}`)}>
                            <img src={`${API_BASE}${img}`} alt={item.design_code} className="w-14 h-14 rounded-lg object-cover border hover:ring-2 hover:ring-emerald-400 transition-all" />
                            {colorName && <p className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[56px]">{colorName}</p>}
                          </div>
                        );
                      }) : (
                        <div className="w-14 h-14 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-bold text-emerald-700">{item.design_code}</span>
                        </div>
                      )}
                    </div>
                    <div>
                      {editPO ? (
                        <div className="relative inline-block">
                          <Input
                            value={item.designSearch ?? item.design_code}
                            onChange={e => {
                              const val = e.target.value.toUpperCase();
                              setPoItems(prev => prev.map((it, i) => i === idx ? { ...it, designSearch: val, showDesignDropdown: !!val } : it));
                            }}
                            onBlur={() => setTimeout(() => setPoItems(prev => prev.map((it, i) => i === idx ? { ...it, showDesignDropdown: false } : it)), 200)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const q = (item.designSearch || '').trim();
                                if (q) {
                                  const exact = products.find(p => p.design_code?.toUpperCase() === q);
                                  if (exact) swapDesignInItem(idx, exact);
                                  else searchDesignFromBackend(q, idx);
                                }
                              }
                            }}
                            className="h-8 w-44 text-sm font-bold"
                            placeholder="Design code..."
                            data-testid={`po-edit-design-code-${idx}`}
                          />
                          {item.showDesignDropdown && (item.designSearch ?? '').length >= 2 && (() => {
                            const q = (item.designSearch || '').toLowerCase();
                            const matches = products.filter(p => p.design_code?.toLowerCase().includes(q)).slice(0, 10);
                            return matches.length > 0 ? (
                              <div className="absolute z-50 w-64 mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto left-0">
                                {matches.map(p => (
                                  <div key={p.id} className="px-3 py-2 hover:bg-emerald-50 cursor-pointer text-sm flex items-center gap-2"
                                    onMouseDown={e => { e.preventDefault(); swapDesignInItem(idx, p); }}>
                                    {p.images?.[0] && <img src={`${API_BASE}${p.images[0]}`} alt="" className="w-8 h-8 rounded object-cover" />}
                                    <span className="font-mono font-semibold">{p.design_code}</span>
                                    <span className="text-gray-400 text-xs">{getBrandName(p.brand_id)}</span>
                                  </div>
                                ))}
                                <div className="px-3 py-2 text-xs text-gray-400 border-t">
                                  Not found? Type full code & press Enter to search catalogue
                                </div>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      ) : (
                        <span className="font-bold text-gray-900 text-lg">Design #{item.design_code}</span>
                      )}
                      {item.brand_id && <Badge className="ml-2 text-xs bg-indigo-100 text-indigo-700">{getBrandName(item.brand_id)}</Badge>}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`}>Remove</Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Brand *</Label>
                    <select value={item.brand_id} onChange={e => updateItemField(idx, 'brand_id', e.target.value)}
                      className="h-9 w-full rounded border border-input bg-background px-2 text-sm" data-testid={`po-item-brand-${idx}`}>
                      <option value="">Select Brand</option>
                      {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Fabric</Label>
                    <select value={item.fabric} onChange={e => updateItemField(idx, 'fabric', e.target.value)}
                      className="h-9 w-full rounded border border-input bg-background px-2 text-sm">
                      <option value="">Select Fabric</option>
                      {(enums.fabrics || []).map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Last Date of Delivery *</Label>
                    <Input type="date" className="h-9 text-sm" value={item.delivery_date}
                      onChange={e => updateItemField(idx, 'delivery_date', e.target.value)} data-testid={`po-item-delivery-${idx}`} required />
                  </div>
                </div>

                {/* Sizes */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold">Select Sizes</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {enums.sizes.map(size => (
                      <button key={size} type="button" onClick={() => toggleSize(idx, size)}
                        className={`px-2.5 py-1 rounded text-xs border transition-colors ${item.selectedSizes?.includes(size) ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400'}`}
                        data-testid={`size-${idx}-${size}`}>{size}</button>
                    ))}
                  </div>
                </div>

                {/* Colors */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold">Select Colors</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {enums.colors.map(color => (
                      <button key={color} type="button" onClick={() => toggleColor(idx, color)}
                        className={`px-2.5 py-1 rounded text-xs border transition-colors ${item.selectedColors?.includes(color) ? 'bg-teal-500 text-white border-teal-500' : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400'}`}
                        data-testid={`color-${idx}-${color}`}>{color}</button>
                    ))}
                  </div>
                  <div className="flex gap-1 items-center max-w-xs">
                    <Input className="h-7 text-xs flex-1" placeholder="Add new colour..." value={newColorInput} onChange={e => setNewColorInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomEnum('colors', newColorInput, setNewColorInput); } }} data-testid={`po-add-new-color-input-${idx}`} />
                    <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => addCustomEnum('colors', newColorInput, setNewColorInput)} disabled={!newColorInput.trim()} data-testid={`po-add-new-color-btn-${idx}`}>+ Add</Button>
                  </div>
                </div>

                {/* Size Group Pricing */}
                {item.selectedSizes?.length > 0 && item.selectedColors?.length > 0 && (
                  <SizeGroupPricing sizes={item.selectedSizes} onApply={(costMap) => applySizeGroupCosts(idx, costMap)} />
                )}

                {/* Variants Table */}
                {item.variants.length > 0 && (
                  <div className="overflow-x-auto">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] text-gray-500">Quick fill:</span>
                      <Button size="sm" variant="outline" className="h-5 px-2 text-[10px]"
                        onClick={() => {
                          const firstQty = item.variants[0]?.qty_ordered;
                          if (firstQty) { item.variants.forEach((_, vi) => updateVariant(idx, vi, 'qty_ordered', firstQty)); }
                        }}
                        data-testid={`po-copy-qty-${idx}`}>Copy Qty to All</Button>
                      <Button size="sm" variant="outline" className="h-5 px-2 text-[10px]"
                        onClick={() => {
                          const firstCost = item.variants[0]?.tentative_cost;
                          if (firstCost) { item.variants.forEach((_, vi) => updateVariant(idx, vi, 'tentative_cost', firstCost)); }
                        }}
                        data-testid={`po-copy-cost-${idx}`}>Copy Cost to All</Button>
                    </div>
                    <table className="w-full text-sm border rounded" data-testid={`variants-table-${idx}`}>
                      <thead className="bg-gray-50"><tr>
                        <th className="p-2.5 text-left">Color</th><th className="p-2.5 text-left">Size</th>
                        <th className="p-2.5 text-right">Qty</th><th className="p-2.5 text-right">Cost/pc</th>
                        <th className="p-2.5 text-right">Total</th>
                      </tr></thead>
                      <tbody>
                        {item.variants.map((v, vi) => (
                          <tr key={vi} className="border-t">
                            <td className="p-2.5">{v.color}</td><td className="p-2.5 font-medium">{v.size}</td>
                            <td className="p-2.5"><Input type="number" className="h-8 w-20 text-sm text-right" value={v.qty_ordered || ''} onChange={e => updateVariant(idx, vi, 'qty_ordered', e.target.value)} min="0" data-testid={`qty-${idx}-${vi}`} /></td>
                            <td className="p-2.5"><Input type="number" className="h-8 w-24 text-sm text-right" value={v.tentative_cost || ''} onChange={e => updateVariant(idx, vi, 'tentative_cost', e.target.value)} min="0" data-testid={`cost-${idx}-${vi}`} /></td>
                            <td className="p-2.5 text-right font-medium">₹{((v.qty_ordered || 0) * (v.tentative_cost || 0)).toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-emerald-50 font-semibold"><tr>
                        <td className="p-2.5" colSpan="2">Design Total</td>
                        <td className="p-2.5 text-right">{item.variants.reduce((s, v) => s + (v.qty_ordered || 0), 0)} pcs</td>
                        <td className="p-2.5"></td>
                        <td className="p-2.5 text-right text-emerald-700">₹{calcItemTotal(item).toLocaleString('en-IN')}</td>
                      </tr></tfoot>
                    </table>
                  </div>
                )}

                <div className="space-y-1">
                  <Label className="text-xs">Design Notes</Label>
                  <Input className="h-8 text-sm" value={item.notes} onChange={e => updateItemField(idx, 'notes', e.target.value)} placeholder="Notes..." />
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Add Product Buttons */}
          {!addMode && (
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setAddMode('repeat')} data-testid="add-repeat-product">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                Select from the Catalogue
              </Button>
              <Button variant="outline" onClick={() => { setAddMode('new'); fetchNextDesignNumber(); }} data-testid="add-new-product">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                New Product
              </Button>
            </div>
          )}

          {/* Repeat Order - Select Existing Design */}
          {addMode === 'repeat' && (
            <Card className="border-dashed"><CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <Label className="font-semibold">Select from existing Designs</Label>
                <Button variant="ghost" size="sm" onClick={() => { setAddMode(null); setRepeatSearch(''); }}>Cancel</Button>
              </div>
              <Input value={repeatSearch} onChange={e => setRepeatSearch(e.target.value)}
                placeholder="Search designs... (press Enter to search full catalogue)" className="h-9 text-sm mb-3" data-testid="po-repeat-search"
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && repeatSearch.trim()) {
                    e.preventDefault();
                    const q = repeatSearch.trim();
                    const local = products.find(p => p.design_code?.toUpperCase() === q.toUpperCase());
                    if (local) { addExistingProductToItems(local); return; }
                    try {
                      const res = await api.get(`/products/search/${encodeURIComponent(q)}`);
                      if (res.data?.length > 0) {
                        setProducts(prev => {
                          const ids = new Set(prev.map(p => p.id));
                          const newOnes = res.data.filter(p => !ids.has(p.id));
                          return newOnes.length > 0 ? [...prev, ...newOnes] : prev;
                        });
                        addExistingProductToItems(res.data[0]);
                      }
                    } catch { toast.error(`Design "${q}" not found in catalogue`); }
                  }
                }}
              />
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 max-h-72 overflow-y-auto">
                {sortedRepeatProducts.map(p => (
                  <button key={p.id} onClick={() => addExistingProductToItems(p)}
                    className="p-2 border rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition-colors text-center" data-testid={`select-product-${p.id}`}>
                    {p.images?.[0] ? <img src={`${API_BASE}${p.images[0]}`} alt={p.design_code} className="w-full h-16 object-cover rounded mb-1" /> : <div className="w-full h-16 bg-gray-100 rounded mb-1 flex items-center justify-center text-xs font-bold text-gray-500">{p.design_code}</div>}
                    <p className="text-xs font-medium truncate">{p.design_code}</p>
                    <p className="text-xs text-gray-400 truncate">{getBrandName(p.brand_id)}</p>
                  </button>
                ))}
              </div>
            </CardContent></Card>
          )}

          {/* New Product Form */}
          {addMode === 'new' && (
            <Card className="border-dashed border-emerald-300"><CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">New Product Details</Label>
                <Button variant="ghost" size="sm" onClick={() => setAddMode(null)}>Cancel</Button>
              </div>
              <div className="flex items-center gap-4">
                {newProduct.images.length > 0 ? (
                  <div className="relative w-20 h-20 rounded-lg overflow-hidden border">
                    <img src={`${API_BASE}${newProduct.images[0]}`} alt="New" className="w-full h-full object-cover" />
                    <button onClick={() => setNewProduct(p => ({ ...p, images: [] }))} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">x</button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-emerald-400" onClick={() => fileInputRef.current?.click()} data-testid="po-new-image-upload">
                    {uploading ? <div className="animate-spin h-5 w-5 border-b-2 border-emerald-600 rounded-full"></div> : <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Design Number *</Label><Input className="h-9 text-sm" value={newProduct.design_code} onChange={e => setNewProduct(p => ({ ...p, design_code: e.target.value }))} data-testid="po-new-design-number" /></div>
                  <div><Label className="text-xs">Category *</Label>
                    <select value={newProduct.category} onChange={e => setNewProduct(p => ({ ...p, category: e.target.value }))} className="h-9 w-full rounded border border-input bg-background px-2 text-sm" data-testid="po-new-category"><option value="">Select</option>{enums.categories.map(c => <option key={c} value={c}>{c}</option>)}</select>
                    <div className="flex gap-1 mt-1"><Input className="h-6 text-[10px] flex-1" placeholder="New category..." value={newCategoryInput} onChange={e => setNewCategoryInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomEnum('categories', newCategoryInput, setNewCategoryInput); } }} data-testid="po-add-new-category-input" /><Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px]" onClick={() => addCustomEnum('categories', newCategoryInput, setNewCategoryInput)} disabled={!newCategoryInput.trim()} data-testid="po-add-new-category-btn">+</Button></div>
                  </div>
                  <div><Label className="text-xs">Brand *</Label><select value={newProduct.brand_id} onChange={e => setNewProduct(p => ({ ...p, brand_id: e.target.value }))} className="h-9 w-full rounded border border-input bg-background px-2 text-sm" data-testid="po-new-brand"><option value="">Select</option>{brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
                  <div><Label className="text-xs">Occasion *</Label>
                    <div className="flex flex-wrap gap-1 mt-0.5" data-testid="po-new-occasion">
                      {enums.design_themes.map(t => {
                        const sel = newProduct.design_theme.includes(t);
                        return <button key={t} type="button" onClick={() => setNewProduct(p => ({ ...p, design_theme: sel ? p.design_theme.filter(x => x !== t) : [...p.design_theme, t] }))} className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${sel ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400'}`}>{sel && '+ '}{t}</button>;
                      })}
                    </div>
                    <div className="flex gap-1 mt-1"><Input className="h-6 text-[10px] flex-1" placeholder="New occasion..." value={newOccasionInput} onChange={e => setNewOccasionInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomEnum('occasions', newOccasionInput, setNewOccasionInput); } }} data-testid="po-add-new-occasion-input" /><Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px]" onClick={() => addCustomEnum('occasions', newOccasionInput, setNewOccasionInput)} disabled={!newOccasionInput.trim()} data-testid="po-add-new-occasion-btn">+</Button></div>
                  </div>
                </div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Fabric</Label>
                <select value={newProduct.fabric} onChange={e => setNewProduct(p => ({ ...p, fabric: e.target.value }))} className="h-9 w-full rounded border border-input bg-background px-2 text-sm"><option value="">Select Fabric</option>{(enums.fabrics || []).map(f => <option key={f} value={f}>{f}</option>)}</select>
                <div className="flex gap-1 mt-1"><Input className="h-6 text-[10px] flex-1" placeholder="New fabric..." value={newFabricInput} onChange={e => setNewFabricInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomEnum('fabrics', newFabricInput, setNewFabricInput); } }} data-testid="po-add-new-fabric-input" /><Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px]" onClick={() => addCustomEnum('fabrics', newFabricInput, setNewFabricInput)} disabled={!newFabricInput.trim()} data-testid="po-add-new-fabric-btn">+</Button></div>
              </div>
              <Button size="sm" onClick={addNewProductToItems} data-testid="po-add-new-product-btn">Add to PO</Button>
            </CardContent></Card>
          )}

          {/* Summary & Submit */}
          {poItems.length > 0 && (
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-5 space-y-4">
                <div className="space-y-1">
                  <Label className="text-sm font-semibold">PO Notes</Label>
                  <Textarea value={poNotes} onChange={e => setPoNotes(e.target.value)} placeholder="General notes..." className="text-sm" data-testid="po-notes" />
                </div>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="text-sm text-gray-600">{poItems.length} design(s) | {calcTotalQty()} pcs</p>
                    <p className="text-2xl font-bold text-gray-900">Total: ₹{calcTotal().toLocaleString('en-IN')}</p>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => navigate('/purchase-orders')} data-testid="cancel-po-button">Cancel</Button>
                    <Button onClick={submitPO} size="lg" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting} data-testid="submit-po-button">
                      {submitting ? 'Saving...' : editPO ? 'Update PO' : 'Create Purchase Order'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Enlarged Image Modal */}
      {enlargedImage && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center" onClick={() => setEnlargedImage(null)} data-testid="enlarged-image-modal">
          <img src={enlargedImage} alt="Enlarged" className="max-w-[80vw] max-h-[80vh] rounded-lg shadow-2xl object-contain" />
        </div>
      )}
    </div>
  );
};

export default CreatePurchaseOrder;
